import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  ArrowsRightLeftIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  LinkSlashIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Link } from '@heroui/link';
import { addToast } from '@heroui/toast';
import { CLOSED_WON_DEAL_STAGE } from '@lib/constants/deal-stages';
import { autoScale, electricity, format } from '@lib/utils/units';
import { useQueryClient } from '@tanstack/react-query';
import { type ErrorComponentProps, Link as RouterLink, createFileRoute, useNavigate } from '@tanstack/react-router';
import { formatDuration, intervalToDuration } from 'date-fns';
import { useMemo, useRef, useState } from 'react';

import { useAttioControllerGetDeal, useAttioControllerSyncContract } from '@/api/generated/attio/attio';
import type { ContractDto, CurrencyDto, ElectricityEnergyDto } from '@/api/generated/schemas';
import {
  ContractDtoSource,
  ContractDtoStatus,
  ContractSiteLinkDtoSiteContractStatus,
  ContractSiteLinkDtoVasStatus,
} from '@/api/generated/schemas';
import { DetailField, Icon, PageLoadingFallback } from '@/components/common';
import {
  ContractLinkedSitesTable,
  ContractSourceChip,
  ContractStatusChip,
  ContractTypeAndSegmentChips,
  DeleteContractModal,
  EditableContractArrayField,
  EditableContractDateField,
  EditableContractIssuerField,
  EditableContractNumberField,
  EditableContractSelectField,
  EditableContractTextField,
  EditableContractValueUnitField,
  LinkContractDealModal,
  ManualMergeModal,
} from '@/components/contracts';
import {
  emptyChargePeriod,
  emptyChargeWindow,
  emptyContractRate,
  emptyDemandChargePeriod,
  emptyEnvironmentalChargePeriod,
  emptyGreenPowerOption,
  renderChargePeriodEdit,
  renderChargePeriodRead,
  renderChargeWindowEdit,
  renderChargeWindowRead,
  renderContractRateEdit,
  renderContractRateRead,
  renderDemandChargePeriodEdit,
  renderDemandChargePeriodRead,
  renderEnvironmentalChargePeriodEdit,
  renderEnvironmentalChargePeriodRead,
  renderGreenPowerOptionEdit,
  renderGreenPowerOptionRead,
  valueUnitDefaults,
  valueUnitOptions,
  valueUnitTypeDiscriminators,
} from '@/components/contracts/editable-fields';
import { AttioPill } from '@/components/crm/AttioPill';
import { SyncStatusCard } from '@/components/sites';
import { downloadFileFromStorage, uploadFileToStorage } from '@/firebase/storage';
import { useContractSites, useSuspenseContract, useUnlinkContractDeal } from '@/queries/contracts.queries';
import {
  getContractStatus,
  getState,
  getStatusLabel,
  isElectricityContract,
  isMeteringContract,
  issuerLabel,
} from '@/utils/contract.utils';
import { formatDateTime } from '@/utils/formatting';

export const Route = createFileRoute('/contracts/$contractId')({
  component: ContractDetailContent,
  pendingComponent: PageLoadingFallback,
  errorComponent: ContractErrorComponent,
});

// RYO/ZOHO/MANUAL collapse to MANUAL on save; only EXTERNAL is a non-WU deal.
const CONTRACT_SOURCE_OPTIONS = [
  { value: ContractDtoSource.MANUAL, label: 'Watt Utilities' },
  { value: ContractDtoSource.EXTERNAL, label: 'External' },
];

const STATE_OPTIONS = [
  { value: 'NSW', label: 'NSW' },
  { value: 'VIC', label: 'VIC' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'WA', label: 'WA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'NT', label: 'NT' },
  { value: 'ACT', label: 'ACT' },
];

const PRICING_STRUCTURE_OPTIONS = [
  { value: 'FLAT', label: 'FLAT' },
  { value: 'STEPPED', label: 'STEPPED' },
];

// Helper functions for formatting unit-value DTOs
function formatEnergy(energy: ElectricityEnergyDto | undefined, decimals = 2): string {
  if (!energy) return '-';
  const unit = electricity.energy(energy.value, energy.unit);
  const scaled = autoScale(unit);
  return format(scaled, decimals);
}

function formatCurrency(currency: CurrencyDto | undefined, decimals = 0): string {
  if (!currency) return '-';
  const value = parseFloat(currency.value);
  if (currency.unit === '$') {
    return `$${value.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }
  return `${value.toLocaleString('en-AU', { maximumFractionDigits: decimals })}${currency.unit}`;
}

function ContractErrorComponent({ error, reset }: ErrorComponentProps) {
  const navigate = useNavigate();

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-lg font-semibold text-gray-900 mb-2">{error.message || 'Contract not found'}</p>
          <p className="text-sm text-gray-500 mb-6">
            The contract you're looking for doesn't exist or you don't have access to it.
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              variant="light"
              startContent={<Icon as={ArrowLeftIcon} size="sm" color="inherit" />}
              onPress={() => navigate({ to: '/contracts' })}
            >
              Back to Contracts
            </Button>
            <Button variant="flat" onPress={() => reset()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DealDisplay({ dealId, contractId }: { dealId: string; contractId: string }) {
  const unlinkDealMutation = useUnlinkContractDeal();
  const { data: deal } = useAttioControllerGetDeal(dealId);

  const handleUnlink = async () => {
    if (confirm('Are you sure you want to unlink this Deal from the contract?')) {
      try {
        await unlinkDealMutation.mutateAsync(contractId);
        addToast({
          title: 'Success',
          description: 'Deal unlinked successfully',
          color: 'success',
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to unlink Deal';
        addToast({
          title: 'Error',
          description: errorMessage,
          color: 'danger',
        });
      }
    }
  };

  if (!deal) {
    return <span className="text-default-500 text-sm">Loading...</span>;
  }

  const attioBaseUrl = import.meta.env.VITE_ATTIO_APP_BASE_URL;
  const dealUrl = attioBaseUrl ? `${attioBaseUrl}/deals/record/${dealId}` : undefined;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {dealUrl ? (
        <a
          href={dealUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group text-primary hover:text-primary-600 inline-flex items-center gap-1"
        >
          <span className="group-hover:underline font-medium">{deal.dealName}</span>
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
        </a>
      ) : (
        <span className="font-medium text-default-700">{deal.dealName}</span>
      )}
      {deal.stage && (
        <Chip size="sm" variant="flat" color="default" className="text-xs">
          {deal.stage}
        </Chip>
      )}
      <Button
        size="sm"
        variant="light"
        className="text-default-600 -ml-1"
        onPress={handleUnlink}
        isLoading={unlinkDealMutation.isPending}
        startContent={<Icon as={LinkSlashIcon} size="sm" color="inherit" />}
      >
        Unlink
      </Button>
    </div>
  );
}

function ContractDetailContent() {
  const { contractId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: contract } = useSuspenseContract(contractId);
  const contractForSourceEdit = useMemo(
    () => ({
      ...contract,
      source: contract.source === ContractDtoSource.EXTERNAL ? ContractDtoSource.EXTERNAL : ContractDtoSource.MANUAL,
    }),
    [contract],
  );
  const { data: siteLinks = [] } = useContractSites(contractId);
  const { data: linkedDeal } = useAttioControllerGetDeal(contract.dealId ?? '', {
    query: { enabled: !!contract.dealId },
  });
  // PD-628. Sites contracted on a deal Attio still shows as open.
  const dealNotMarkedWon =
    linkedDeal?.stage != null &&
    linkedDeal.stage !== CLOSED_WON_DEAL_STAGE &&
    contract.archivedAt == null &&
    contract.status !== ContractDtoStatus.Expired &&
    siteLinks.some((l) => l.siteContractStatus === ContractSiteLinkDtoSiteContractStatus.Active);

  // VAS rollup (PD-507): VAS lives per-site on the links now, so the contract
  // header/detail show an aggregate. "Varies by site" when active sites disagree.
  const vasRollup = useMemo(() => {
    const active = siteLinks.filter((l) => l.vasStatus === ContractSiteLinkDtoVasStatus.Active);
    const products = new Set(active.map((l) => l.vasProduct ?? ''));
    const costs = new Set(active.map((l) => l.vasDailyCost ?? 0));
    const product = active.length === 0 ? 'No VAS' : products.size === 1 ? [...products][0] || '—' : 'Varies by site';
    const cost = active.length === 0 ? '—' : costs.size === 1 ? `$${[...costs][0].toFixed(2)}/day` : 'Varies by site';
    return { vasSiteCount: active.length, totalSiteCount: siteLinks.length, product, cost };
  }, [siteLinks]);

  const syncContractMutation = useAttioControllerSyncContract();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLinkDealModalOpen, setIsLinkDealModalOpen] = useState(false);
  const [isManualMergeModalOpen, setIsManualMergeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleCopyContractId = async () => {
    try {
      await navigator.clipboard.writeText(contract.id);
      addToast({ title: 'Copied', description: 'Contract ID copied to clipboard', color: 'success' });
    } catch {
      addToast({ title: 'Copy failed', description: 'Could not access clipboard', color: 'danger' });
    }
  };

  const state = getState(contract);
  const daysRemaining = calculateDaysRemaining(contract);
  const electricityContract = isElectricityContract(contract) ? contract : null;
  const meteringContract = isMeteringContract(contract) ? contract : null;

  const handleDownload = async () => {
    if (!contract.file?.storagePath) return;
    try {
      await downloadFileFromStorage(contract.file.storagePath, contract.file.fileName);
    } catch (error) {
      console.error('Error downloading contract:', error);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const storagePath = `contracts/${contractId}/${file.name}`;
      await uploadFileToStorage(storagePath, file);
      await queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isArchived = contract.archivedAt != null;

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
        {isArchived && (
          <div className="flex items-start gap-3 p-4 rounded-medium bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
            <Icon as={ExclamationTriangleIcon} size="md" color="danger" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-danger">This contract has been deleted</p>
              <p className="text-xs text-default-600 mt-1">
                Archived on{' '}
                {new Date(contract.archivedAt!).toLocaleString('en-AU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                . It no longer appears in contract listings or syncs to Attio, and the linked sites no longer use it as
                a source of truth.
              </p>
            </div>
          </div>
        )}
        {dealNotMarkedWon && (
          <div className="flex items-start gap-3 p-4 rounded-medium bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
            <Icon as={ExclamationTriangleIcon} size="md" color="warning" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-warning-700">Deal not marked won</p>
              <p className="text-xs text-default-600 mt-1">
                This contract has active sites, but its deal {linkedDeal?.dealName} is at "{linkedDeal?.stage}" in
                Attio. Confirm the deal is won and move it to Closed Won.
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            isIconOnly
            variant="light"
            onPress={() => navigate({ to: '/contracts' })}
            aria-label="Back to contracts"
          >
            <Icon as={ArrowLeftIcon} size="md" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{issuerLabel(contract.issuer)}</h1>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                aria-label="Copy contract ID"
                title="Copy contract ID"
                onPress={handleCopyContractId}
              >
                <Icon as={ClipboardDocumentIcon} size="sm" />
              </Button>
              {contract.attioRecordId && (
                <AttioPill
                  href={`${import.meta.env.VITE_ATTIO_APP_BASE_URL}/custom/contracts/record/${contract.attioRecordId}`}
                />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <ContractTypeAndSegmentChips contract={contract} size="sm" variant="flat" />
              {state !== 'N/A' && (
                <Chip size="sm" variant="flat" color="default">
                  {state}
                </Chip>
              )}
              <ContractSourceChip contract={contract} size="sm" variant="flat" />
            </div>
          </div>
          {!isArchived && (
            <>
              <Button
                variant="flat"
                color="warning"
                startContent={<Icon as={ArrowsRightLeftIcon} size="sm" color="inherit" />}
                onPress={() => setIsManualMergeModalOpen(true)}
              >
                Merge with another contract
              </Button>
              <Button
                variant="flat"
                color="danger"
                startContent={<Icon as={TrashIcon} size="sm" color="inherit" />}
                onPress={() => setIsDeleteModalOpen(true)}
              >
                Delete
              </Button>
            </>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800">
            <CardBody className="gap-1 py-4">
              <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Status</p>
              <div className="flex flex-col gap-1 mt-1">
                <ContractStatusChip contract={contract} size="lg" />
                <p className="text-xs text-default-500">{daysRemaining}</p>
              </div>
            </CardBody>
          </Card>

          <Card className="bg-gradient-to-br from-success-50 to-success-100 dark:from-success-900 dark:to-success-800">
            <CardBody className="gap-1 py-4">
              <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Sites</p>
              <p className="text-3xl font-bold mt-1">{siteLinks.length}</p>
            </CardBody>
          </Card>

          {meteringContract && (
            <Card className="bg-gradient-to-br from-secondary-50 to-secondary-100 dark:from-secondary-900 dark:to-secondary-800">
              <CardBody className="gap-1 py-4">
                <p className="text-xs font-medium text-default-600 uppercase tracking-wide">VAS Sites</p>
                <p className="text-3xl font-bold mt-1">
                  {vasRollup.vasSiteCount} of {vasRollup.totalSiteCount}
                </p>
              </CardBody>
            </Card>
          )}

          {electricityContract?.consumptionEstimate && (
            <Card className="bg-gradient-to-br from-secondary-50 to-secondary-100 dark:from-secondary-900 dark:to-secondary-800">
              <CardBody className="gap-1 py-4">
                <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Est. Annual Usage</p>
                <p className="text-3xl font-bold mt-1">
                  {formatEnergy(electricityContract.consumptionEstimate.annual)}
                </p>
              </CardBody>
            </Card>
          )}

          {electricityContract?.estimatedAnnualCost && (
            <Card className="bg-gradient-to-br from-warning-50 to-warning-100 dark:from-warning-900 dark:to-warning-800">
              <CardBody className="gap-1 py-4">
                <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Est. Annual Cost</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(electricityContract.estimatedAnnualCost)}</p>
              </CardBody>
            </Card>
          )}

          {(!electricityContract?.consumptionEstimate || !electricityContract?.estimatedAnnualCost) && (
            <Card className="bg-gradient-to-br from-default-50 to-default-100 dark:from-default-900 dark:to-default-800">
              <CardBody className="gap-1 py-4">
                <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Time Left</p>
                <p className="text-3xl font-bold mt-1">{daysRemaining}</p>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Provenance banner — only shown for non-RYO sources with notes */}
        {contract.source && contract.source !== 'RYO' && contract.sourceNotes && (
          <Card className="border border-warning-200 bg-warning-50 dark:bg-warning-900/30">
            <CardBody className="py-3">
              <div className="flex items-start gap-3">
                <ContractSourceChip contract={contract} size="sm" variant="flat" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-default-600 uppercase tracking-wide">Import notes</p>
                  <p className="text-sm whitespace-pre-line mt-1">{contract.sourceNotes}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Contract Details */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Contract Details</h2>
          </CardHeader>
          <Divider />
          <CardBody className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              <EditableContractIssuerField label="Issuer" contractId={contract.id} contract={contract} />
              <EditableContractTextField
                label="Contract Name"
                fieldPath="contractName"
                contractId={contract.id}
                contract={contract}
                placeholder="Contract display name"
              />
              <DetailField label="Status" value={getStatusLabel(getContractStatus(contract))} />
              <EditableContractSelectField
                label="Contract with"
                fieldPath="source"
                contractId={contract.id}
                contract={contractForSourceEdit}
                options={CONTRACT_SOURCE_OPTIONS}
              />
              <DetailField label="Contract Type" value={contract.contractType} />
              {electricityContract?.marketSegment && (
                <DetailField label="Market Segment" value={electricityContract.marketSegment} />
              )}
              {electricityContract && (
                <EditableContractSelectField
                  label="State"
                  fieldPath="state"
                  contractId={contract.id}
                  contract={contract}
                  options={STATE_OPTIONS}
                />
              )}
              {meteringContract && (
                <>
                  <EditableContractTextField
                    label="Metering Product"
                    fieldPath="meteringProduct"
                    contractId={contract.id}
                    contract={contract}
                  />
                  <EditableContractTextField
                    label="Metering Type"
                    fieldPath="meteringType"
                    contractId={contract.id}
                    contract={contract}
                  />
                  <EditableContractNumberField
                    label="Daily Cost"
                    fieldPath="dailyCost"
                    contractId={contract.id}
                    contract={contract}
                    min={0}
                    step={0.01}
                    formatter={(v) => `$${v.toFixed(2)}/day`}
                  />
                  {/* VAS is per-site (PD-507) — edit it on each site row in Included Sites below. */}
                  <DetailField
                    label="VAS Product"
                    value={
                      <div>
                        <span>{vasRollup.product}</span>
                        <p className="text-xs text-default-400">
                          {vasRollup.vasSiteCount} of {vasRollup.totalSiteCount} sites — see Included Sites
                        </p>
                      </div>
                    }
                  />
                  <DetailField label="VAS Daily Cost" value={vasRollup.cost} />
                </>
              )}
              <EditableContractDateField
                label="Start Date"
                fieldPath="startDate"
                contractId={contract.id}
                contract={contract}
                isRequired
              />
              <EditableContractDateField
                label="End Date"
                fieldPath="endDate"
                contractId={contract.id}
                contract={contract}
                isRequired
              />
              <DetailField label="Duration" value={calculateDuration(contract.startDate, contract.endDate)} />
              <EditableContractTextField
                label="Group Contract"
                fieldPath="groupContractId"
                contractId={contract.id}
                contract={contract}
                placeholder="Link to a group contract ID"
              />
              <DetailField
                label="CRM Deal"
                value={
                  contract.dealId ? (
                    <DealDisplay dealId={contract.dealId} contractId={contractId} />
                  ) : (
                    <Button size="sm" variant="flat" color="primary" onPress={() => setIsLinkDealModalOpen(true)}>
                      Link Deal
                    </Button>
                  )
                }
              />
              <DetailField
                label="Renewal"
                value={
                  contract.renewalSiteList ? (
                    <RouterLink
                      to="/site-lists/$siteListId"
                      params={{ siteListId: contract.renewalSiteList.siteListId }}
                      className="text-primary hover:text-primary-600 inline-flex items-center gap-1"
                    >
                      <span className="hover:underline">
                        Renewal for{' '}
                        {new Date(contract.renewalSiteList.forEndDate).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    </RouterLink>
                  ) : (
                    <span className="text-default-400">Not raised</span>
                  )
                }
              />
              <DetailField
                label="Contract Document"
                value={
                  contract.file ? (
                    <div className="flex items-center gap-2">
                      <Link size="sm" onPress={handleDownload}>
                        {contract.file.fileName}
                      </Link>
                      <span className="text-xs text-default-400">
                        ({(contract.file.fileSize / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                  ) : contract.ryoFileId ? (
                    <a
                      href={`${import.meta.env.VITE_RYO_BASE_URL}/?r=file/preview&file_id=${contract.ryoFileId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-1 text-primary hover:text-primary-600"
                    >
                      <span className="group-hover:underline text-sm">View in RYO</span>
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 flex-shrink-0" />
                    </a>
                  ) : (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button
                        size="sm"
                        variant="light"
                        startContent={<Icon as={ArrowUpTrayIcon} size="sm" color="inherit" />}
                        onPress={() => fileInputRef.current?.click()}
                        isLoading={isUploading}
                      >
                        Upload
                      </Button>
                    </>
                  )
                }
              />
            </div>

            <div className="pt-4 border-t border-default-200 mt-4">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-default-400">Created</p>
                  <p className="font-medium">{formatDateTime(contract.createdAt)}</p>
                </div>
                <div>
                  <p className="text-default-400">Updated</p>
                  <p className="font-medium">{formatDateTime(contract.updatedAt)}</p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Electricity-Specific Details */}
        {electricityContract && <ElectricityContractDetails contract={electricityContract} />}

        {/* Sync Status */}
        <SyncStatusCard
          attioSync={contract.attioSync}
          ryoContractsSync={contract.ryoContractsSync}
          onSyncToAttio={() => syncContractMutation.mutate({ contractId })}
          isSyncingToAttio={syncContractMutation.isPending}
        />

        {/* Linked Sites */}
        <ContractLinkedSitesTable contract={contract} />
      </div>

      {/* Link Deal Modal */}
      <LinkContractDealModal
        isOpen={isLinkDealModalOpen}
        onClose={() => setIsLinkDealModalOpen(false)}
        contractId={contractId}
      />

      {/* Manual Merge Modal */}
      <ManualMergeModal
        isOpen={isManualMergeModalOpen}
        onClose={() => setIsManualMergeModalOpen(false)}
        currentContract={contract}
      />

      {/* Delete Contract Modal */}
      <DeleteContractModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        contract={contract}
        onDeleted={() => navigate({ to: '/contracts' })}
      />
    </div>
  );
}

function isCIContract(contract: ContractDto): boolean {
  return isElectricityContract(contract) && contract.marketSegment === 'C&I';
}

function isSMEContract(contract: ContractDto): boolean {
  return isElectricityContract(contract) && contract.marketSegment === 'SME';
}

function ElectricityContractDetails({ contract }: { contract: ContractDto }) {
  const isCi = isCIContract(contract);
  const isSme = isSMEContract(contract);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commercial Terms */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Commercial Terms</h2>
          </CardHeader>
          <Divider />
          <CardBody className="gap-4">
            <EditableContractValueUnitField
              label="Annual Consumption Estimate"
              fieldPath="consumptionEstimate.annual"
              contractId={contract.id}
              contract={contract}
              units={valueUnitOptions.energy}
              defaultUnit={valueUnitDefaults.energy}
              typeDiscriminator={valueUnitTypeDiscriminators.energy}
            />
            <EditableContractValueUnitField
              label="Average Blended Rate"
              fieldPath="averageBlendedRate"
              contractId={contract.id}
              contract={contract}
              units={valueUnitOptions.energyRate}
              defaultUnit={valueUnitDefaults.energyRate}
              typeDiscriminator={valueUnitTypeDiscriminators.energyRate}
            />
            <EditableContractValueUnitField
              label="Estimated Annual Cost"
              fieldPath="estimatedAnnualCost"
              contractId={contract.id}
              contract={contract}
              units={valueUnitOptions.currency}
              defaultUnit={valueUnitDefaults.currency}
              typeDiscriminator={valueUnitTypeDiscriminators.currency}
            />
            {isCi && (
              <>
                <EditableContractNumberField
                  label="Min Tolerance"
                  fieldPath="consumptionToleranceBands.minPercent"
                  contractId={contract.id}
                  contract={contract}
                  min={0}
                  max={100}
                  step={0.01}
                  formatter={(v) => `${v}%`}
                />
                <EditableContractNumberField
                  label="Max Tolerance"
                  fieldPath="consumptionToleranceBands.maxPercent"
                  contractId={contract.id}
                  contract={contract}
                  min={0}
                  max={100}
                  step={0.01}
                  formatter={(v) => `${v}%`}
                />
              </>
            )}
            {isSme && (
              <>
                <EditableContractNumberField
                  label="Discount"
                  fieldPath="discountPercent"
                  contractId={contract.id}
                  contract={contract}
                  min={0}
                  max={100}
                  step={0.01}
                  formatter={(v) => `${v}%`}
                />
                <EditableContractValueUnitField
                  label="Daily Supply Charge"
                  fieldPath="dailySupplyCharge"
                  contractId={contract.id}
                  contract={contract}
                  units={valueUnitOptions.supplyCharge}
                  defaultUnit={valueUnitDefaults.supplyCharge}
                  typeDiscriminator={valueUnitTypeDiscriminators.supplyCharge}
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Pricing Structure */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Pricing Structure</h2>
          </CardHeader>
          <Divider />
          <CardBody className="gap-4">
            <EditableContractSelectField
              label="Structure Type"
              fieldPath="pricingStructure"
              contractId={contract.id}
              contract={contract}
              options={PRICING_STRUCTURE_OPTIONS}
            />
            <EditableContractArrayField
              label="Charge Periods"
              fieldPath="chargePeriods"
              contractId={contract.id}
              contract={contract}
              items={contract.chargePeriods}
              createEmpty={emptyChargePeriod}
              renderReadItem={renderChargePeriodRead}
              renderEditItem={renderChargePeriodEdit}
              emptyMessage="No charge periods defined"
            />
          </CardBody>
        </Card>
      </div>

      {/* Environmental Charges (C&I Only) */}
      {isCi && (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Environmental Charges</h2>
          </CardHeader>
          <Divider />
          <CardBody className="gap-4">
            <EditableContractArrayField
              label="Environmental Charges"
              fieldPath="environmentalCharges"
              contractId={contract.id}
              contract={contract}
              items={contract.environmentalCharges}
              createEmpty={emptyEnvironmentalChargePeriod}
              renderReadItem={renderEnvironmentalChargePeriodRead}
              renderEditItem={renderEnvironmentalChargePeriodEdit}
              emptyMessage="No environmental charges defined"
            />
          </CardBody>
        </Card>
      )}

      {/* Charge Windows (Time of Use) */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Charge Windows (Time of Use)</h2>
        </CardHeader>
        <Divider />
        <CardBody className="gap-4">
          <EditableContractArrayField
            label="Charge Windows"
            fieldPath="chargeWindows"
            contractId={contract.id}
            contract={contract}
            items={contract.chargeWindows}
            createEmpty={emptyChargeWindow}
            renderReadItem={renderChargeWindowRead}
            renderEditItem={renderChargeWindowEdit}
            emptyMessage="No charge windows defined"
          />
        </CardBody>
      </Card>

      {/* Demand Charges */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Demand Charges</h2>
        </CardHeader>
        <Divider />
        <CardBody className="gap-4">
          <EditableContractArrayField
            label="Demand Charges"
            fieldPath="demandCharges"
            contractId={contract.id}
            contract={contract}
            items={contract.demandCharges}
            createEmpty={emptyDemandChargePeriod}
            renderReadItem={renderDemandChargePeriodRead}
            renderEditItem={renderDemandChargePeriodEdit}
            emptyMessage="No demand charges defined"
          />
        </CardBody>
      </Card>

      {/* Export Rates */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Export Rates (Solar)</h2>
        </CardHeader>
        <Divider />
        <CardBody className="gap-4">
          <EditableContractArrayField
            label="Export Rates"
            fieldPath="exportRates"
            contractId={contract.id}
            contract={contract}
            items={contract.exportRates}
            createEmpty={emptyChargePeriod}
            renderReadItem={renderChargePeriodRead}
            renderEditItem={renderChargePeriodEdit}
            emptyMessage="No export rates defined"
          />
        </CardBody>
      </Card>

      {/* Green Power Options */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Green Power Options</h2>
        </CardHeader>
        <Divider />
        <CardBody className="gap-4">
          <EditableContractArrayField
            label="Green Power Options"
            fieldPath="greenPowerOptions"
            contractId={contract.id}
            contract={contract}
            items={contract.greenPowerOptions}
            createEmpty={emptyGreenPowerOption}
            renderReadItem={renderGreenPowerOptionRead}
            renderEditItem={renderGreenPowerOptionEdit}
            emptyMessage="No green power options defined"
          />
        </CardBody>
      </Card>

      {/* Rates */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Rate Charges</h2>
        </CardHeader>
        <Divider />
        <CardBody className="gap-4">
          <EditableContractArrayField
            label="Rate Charges"
            fieldPath="rates"
            contractId={contract.id}
            contract={contract}
            items={contract.rates}
            createEmpty={emptyContractRate}
            renderReadItem={renderContractRateRead}
            renderEditItem={renderContractRateEdit}
            emptyMessage="No rate charges defined"
          />
        </CardBody>
      </Card>
    </>
  );
}

function calculateDuration(startDate: Date, endDate: Date): string {
  const duration = intervalToDuration({ start: startDate, end: endDate });
  return formatDuration(duration, { format: ['years', 'months', 'days'] });
}

function calculateDaysRemaining(contract: ContractDto): string {
  const now = new Date();

  if (now < contract.startDate) {
    const duration = intervalToDuration({ start: now, end: contract.startDate });
    const formatted = formatDuration(duration, { format: ['years', 'months', 'days'] });
    return `Starts in ${formatted}`;
  } else if (now > contract.endDate) {
    const duration = intervalToDuration({ start: contract.endDate, end: now });
    const formatted = formatDuration(duration, { format: ['years', 'months', 'days'] });
    return `Expired ${formatted} ago`;
  } else {
    const duration = intervalToDuration({ start: now, end: contract.endDate });
    const formatted = formatDuration(duration, { format: ['years', 'months', 'days'] });
    return `${formatted} remaining`;
  }
}
